import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { HeroSection } from '../components/HeroSection';
import { PricingSection } from '../components/PricingSection';
import { ScanProgress } from '../components/ScanProgress';
import { LimitedResultsView } from '../components/LimitedResultsView';
import { FullResultsView } from '../components/FullResultsView';
import { api } from '../lib/api';
import type { ScanResults } from '@wrm/shared';
import styles from './LandingPage.module.css';

type PageState = 'idle' | 'scanning' | 'results' | 'error';

export function LandingPage() {
  const { isSignedIn, getToken } = useAuth();
  const [pageState, setPageState] = useState<PageState>('idle');
  const [scanUrl, setScanUrl] = useState('');
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStatus, setScanStatus] = useState('');
  const [scanResults, setScanResults] = useState<ScanResults | null>(null);
  const [isAuthenticatedScan, setIsAuthenticatedScan] = useState(false);
  const [userTier, setUserTier] = useState<string>('free');
  const [error, setError] = useState('');

  const handleScanSubmit = async (url: string) => {
    setScanUrl(url);
    setPageState('scanning');
    setScanProgress(0);
    setScanStatus('Analyzing website security...');
    setError('');

    try {
      // Simulate progress while waiting for scan
      const progressInterval = setInterval(() => {
        setScanProgress(prev => {
          if (prev >= 90) return prev;
          const stages = ['Checking security...', 'Analyzing performance...', 'Evaluating technical aspects...'];
          const stageIndex = Math.floor((prev / 100) * stages.length);
          setScanStatus(stages[Math.min(stageIndex, stages.length - 1)] ?? 'Scanning...');
          return prev + 10;
        });
      }, 500);

      let data: { status: string; results?: ScanResults; error?: { message: string } };

      // Check if user is logged in - use authenticated scan for full results
      if (isSignedIn) {
        const token = await getToken();
        if (token) {
          // Fetch user's tier
          try {
            const subInfo = await api.getSubscriptionStatus(token);
            setUserTier(subInfo.tier || 'free');
          } catch {
            setUserTier('free');
          }

          const result = await api.startScan(url, token);
          data = {
            status: result.status,
            results: result.results as ScanResults | undefined
          };
          setIsAuthenticatedScan(true);
        } else {
          // Fallback to anonymous scan
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/api/scan/anonymous`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url }),
          });
          data = await response.json();
          setIsAuthenticatedScan(false);
        }
      } else {
        // Anonymous scan for non-logged in users
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/api/scan/anonymous`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url }),
        });
        data = await response.json();
        setIsAuthenticatedScan(false);
      }

      clearInterval(progressInterval);

      if (data.status === 'complete' && data.results) {
        setScanResults(data.results);
        setScanProgress(100);
        setPageState('results');
      } else if (data.error) {
        throw new Error(data.error.message || 'Scan failed');
      } else {
        throw new Error('Scan failed. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setPageState('error');
    }
  };

  const handleScanAnother = () => {
    setPageState('idle');
    setScanUrl('');
    setScanProgress(0);
    setScanResults(null);
    setIsAuthenticatedScan(false);
    setError('');
  };

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.main}>
        {pageState === 'idle' && (
          <>
            <HeroSection onScanSubmit={handleScanSubmit} isScanning={false} />
            <PricingSection />
          </>
        )}

        {pageState === 'scanning' && (
          <ScanProgress
            url={scanUrl}
            progress={scanProgress}
            status={scanStatus}
          />
        )}

        {pageState === 'results' && scanResults && (
          isAuthenticatedScan ? (
            <FullResultsView
              results={scanResults}
              onScanAnother={handleScanAnother}
              tier={userTier}
            />
          ) : (
            <LimitedResultsView
              results={scanResults}
              onScanAnother={handleScanAnother}
            />
          )
        )}

        {pageState === 'error' && (
          <div className={styles.errorContainer}>
            <div className={styles.errorCard}>
              <h2 className={styles.errorTitle}>Scan Failed</h2>
              <p className={styles.errorMessage}>{error}</p>
              <button onClick={handleScanAnother} className="btn btn-primary">
                Try Again
              </button>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
